import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  updateProfile as fbUpdateProfile,
  GoogleAuthProvider,
} from 'firebase/auth';
import { deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, firestore, useFirebaseAuth } from '../firebase';
import type { AuthApi, AuthRole, AuthState, AuthUser } from './types';
import { bootstrapAppUsersIfEmpty, listAppUsers, recordSignIn } from '../db/appUsers';
import { db, nowIso } from '../db/store';

const AuthContext = createContext<AuthApi | null>(null);

const DEFAULT_ROLE: AuthRole = 'staff';
const VALID_ROLES: readonly AuthRole[] = ['admin', 'staff', 'viewer'];

// --- Local mock provider --------------------------------------------------
// Active when `useFirebaseAuth` is false (i.e. no Firebase config / flag
// off) so the app is usable in local dev. Validates against the seeded
// `appUsers` collection using a single shared demo password, and persists
// the session to localStorage so a reload keeps you signed in. This is the
// behavior the `.env.example` comments promised but that never existed.
const MOCK_SESSION_KEY = 'argo-os:auth-session';
const MOCK_PASSWORD = 'argoiq';

// --- TEMPORARY login bypass ------------------------------------------------
// A separate, mode-independent key so "Continue without signing in" works
// whether the app is in mock OR Firebase mode, and survives reloads. When
// present it short-circuits the whole auth wiring on mount. Cleared on
// sign-out. Remove this block (and continueWithoutAuth) when the preview
// should require real auth again.
const BYPASS_SESSION_KEY = 'argo-os:auth-bypass';

function loadBypassSession(): AuthUser | null {
  try {
    const raw = localStorage.getItem(BYPASS_SESSION_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function loadMockSession(): AuthUser | null {
  try {
    const raw = localStorage.getItem(MOCK_SESSION_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function saveMockSession(user: AuthUser | null): void {
  if (user) localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(user));
  else localStorage.removeItem(MOCK_SESSION_KEY);
}

/**
 * Ensure this user has an `appUsers/{uid}` record and return their role.
 *
 * Done entirely client-side (Firestore writes are gated by the `isArgo()`
 * security rule) so there's no public Cloud Function endpoint to invoke —
 * which is what lets this work under the org's Domain-Restricted-Sharing
 * policy. On first sign-in we create the record (default role `staff`);
 * on later sign-ins we just stamp `lastSignInAt`. The Firestore doc is the
 * source of truth for role — no custom claims involved.
 */
async function provisionUserClient(user: {
  uid: string;
  email: string | null;
  displayName: string | null;
}): Promise<AuthRole> {
  if (!firestore) return 'viewer';
  const email = (user.email || '').toLowerCase();
  // Non-argoiq accounts can't write app data (rules reject them); treat as
  // read-only viewers rather than attempting a write that will fail.
  if (!email.endsWith('@argoiq.com')) return 'viewer';

  const ref = doc(firestore, 'appUsers', user.uid);
  const now = new Date().toISOString();

  try {
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const existing = snap.data() as { role?: string; displayName?: string };
      const role = VALID_ROLES.includes(existing.role as AuthRole)
        ? (existing.role as AuthRole)
        : DEFAULT_ROLE;
      await setDoc(
        ref,
        {
          email,
          displayName: user.displayName || existing.displayName || email.split('@')[0],
          lastSignInAt: now,
          updatedAt: now,
        },
        { merge: true },
      );
      return role;
    }

    // First sign-in. If an admin pre-invited this email, the invite record
    // is keyed by the lowercased email (see inviteAppUser): adopt its role
    // into the new uid-keyed record, then consume the invite so Admin >
    // Users doesn't show a duplicate row. firestore.rules independently
    // re-verifies the adopted role against the same invite doc, so a
    // tampered client can't use this path to self-escalate.
    let role: AuthRole = DEFAULT_ROLE;
    let displayName = user.displayName || email.split('@')[0];
    let notes: string | undefined;
    const inviteRef = doc(firestore, 'appUsers', email);
    const invite = await getDoc(inviteRef);
    let consumedInvite = false;
    if (invite.exists()) {
      const data = invite.data() as {
        role?: string;
        status?: string;
        displayName?: string;
        notes?: string;
      };
      if (data.status !== 'disabled' && VALID_ROLES.includes(data.role as AuthRole)) {
        role = data.role as AuthRole;
        displayName = user.displayName || data.displayName || displayName;
        notes = data.notes;
        consumedInvite = true;
      }
    }

    await setDoc(ref, {
      id: user.uid,
      email,
      displayName,
      role,
      ...(notes ? { notes } : {}),
      status: 'active',
      createdAt: now,
      updatedAt: now,
      lastSignInAt: now,
    });
    if (consumedInvite) await deleteDoc(inviteRef).catch(() => undefined);
    return role;
  } catch (err) {
    console.warn('Client-side provisioning failed; defaulting role', err);
    return DEFAULT_ROLE;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    // TEMPORARY bypass takes precedence in any mode: if the user hit
    // "Continue without signing in", restore that synthetic session and
    // skip the real auth wiring entirely so it persists across reloads.
    const bypass = loadBypassSession();
    if (bypass) {
      setState({ status: 'signed-in', user: bypass });
      return;
    }

    // Mock path: no Firebase Auth — restore any persisted local session.
    if (!useFirebaseAuth) {
      bootstrapAppUsersIfEmpty().catch(() => undefined);
      const existing = loadMockSession();
      setState(
        existing
          ? { status: 'signed-in', user: existing }
          : { status: 'signed-out' },
      );
      return;
    }

    if (!auth) {
      console.warn('Firebase Auth is not initialized.');
      setState({ status: 'signed-out' });
      return;
    }

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({ status: 'signed-out' });
        return;
      }

      // Ensure this user has an appUsers record and resolve their role
      // from Firestore (the source of truth). Runs client-side — no Cloud
      // Function call, so nothing for the org policy to block.
      const role = await provisionUserClient({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
      });

      const authUser: AuthUser = {
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL || undefined,
        role,
        lastSignInAt: user.metadata.lastSignInTime || new Date().toISOString(),
      };

      setState({ status: 'signed-in', user: authUser });
    });

    return unsub;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!useFirebaseAuth) {
      // Mock: match a seeded appUsers record by email + shared demo password.
      await bootstrapAppUsersIfEmpty().catch(() => undefined);
      const users = await listAppUsers();
      const match = users.find(
        (u) => u.email.toLowerCase() === email.trim().toLowerCase(),
      );
      if (!match) throw new Error('No account for that email.');
      if (match.status === 'disabled') throw new Error('This account is disabled.');
      if (password !== MOCK_PASSWORD) {
        throw new Error('Incorrect password. (Local dev password: argoiq)');
      }
      const role = VALID_ROLES.includes(match.role as AuthRole)
        ? (match.role as AuthRole)
        : DEFAULT_ROLE;
      const authUser: AuthUser = {
        uid: match.id,
        email: match.email,
        displayName: match.displayName || match.email.split('@')[0],
        role,
        lastSignInAt: nowIso(),
      };
      await recordSignIn(match.email).catch(() => undefined);
      saveMockSession(authUser);
      setState({ status: 'signed-in', user: authUser });
      return;
    }
    if (!auth) throw new Error('Firebase Auth is not initialized');
    // Record creation + lastSignInAt stamping is handled centrally by
    // provisionUserClient() in the onAuthStateChanged handler above.
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!useFirebaseAuth) {
      // Mock: sign in as the first seeded admin (no popup in dev).
      await bootstrapAppUsersIfEmpty().catch(() => undefined);
      const users = await listAppUsers();
      const admin =
        users.find((u) => u.role === 'admin' && u.status !== 'disabled') ??
        users[0];
      if (!admin) throw new Error('No seeded users to sign in as.');
      const authUser: AuthUser = {
        uid: admin.id,
        email: admin.email,
        displayName: admin.displayName || admin.email.split('@')[0],
        role: admin.role as AuthRole,
        lastSignInAt: nowIso(),
      };
      await recordSignIn(admin.email).catch(() => undefined);
      saveMockSession(authUser);
      setState({ status: 'signed-in', user: authUser });
      return;
    }
    if (!auth) throw new Error('Firebase Auth is not initialized');
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ hd: 'argoiq.com' });
    await signInWithPopup(auth, provider);
  }, []);

  const continueWithoutAuth = useCallback(async () => {
    // Synthetic admin session — no credentials, no network. Admin role so
    // every nav surface is reachable while auth is bypassed.
    const guest: AuthUser = {
      uid: 'dev-bypass-admin',
      email: 'guest@argoiq.com',
      displayName: 'Guest (bypass)',
      role: 'admin',
      lastSignInAt: nowIso(),
    };
    localStorage.setItem(BYPASS_SESSION_KEY, JSON.stringify(guest));
    setState({ status: 'signed-in', user: guest });
  }, []);

  const signOut = useCallback(async () => {
    // Always clear the temporary bypass session, regardless of mode.
    localStorage.removeItem(BYPASS_SESSION_KEY);
    if (!useFirebaseAuth) {
      saveMockSession(null);
      setState({ status: 'signed-out' });
      return;
    }
    if (!auth) {
      setState({ status: 'signed-out' });
      return;
    }
    await fbSignOut(auth);
    // Explicit reset: when a bypass session was active the mount effect
    // returned early and never subscribed onAuthStateChanged, so nothing
    // else would flip state back to signed-out. Harmless in the normal
    // path (the listener sets the same state).
    setState({ status: 'signed-out' });
  }, []);

  const updateProfile = useCallback(
    async (patch: { displayName?: string; photoURL?: string }) => {
      if (!useFirebaseAuth) {
        // Mock: patch the persisted session + the appUsers record.
        setState((prev) => {
          if (prev.status !== 'signed-in') return prev;
          const nextDisplayName =
            patch.displayName?.trim() || prev.user.displayName;
          const photoChanged = patch.photoURL !== undefined;
          const next: AuthUser = {
            ...prev.user,
            displayName: nextDisplayName,
            photoURL: photoChanged
              ? patch.photoURL || undefined
              : prev.user.photoURL,
          };
          saveMockSession(next);
          db.update('appUsers', next.uid, {
            displayName: nextDisplayName,
            updatedAt: nowIso(),
          }).catch(() => undefined);
          return { status: 'signed-in', user: next };
        });
        return;
      }

      const current = auth?.currentUser;
      if (!current) return;

      // `photoURL: ''` is an explicit "remove the photo" signal; `undefined`
      // means "leave it untouched". Firebase Auth accepts `null` to clear.
      const nextDisplayName = patch.displayName?.trim() || current.displayName || '';
      const photoChanged = patch.photoURL !== undefined;
      const nextPhotoURL = photoChanged
        ? patch.photoURL || ''
        : current.photoURL || '';

      // 1. Mirror onto the Firebase Auth user (drives auth.currentUser.*).
      await fbUpdateProfile(current, {
        displayName: nextDisplayName,
        ...(photoChanged ? { photoURL: nextPhotoURL || null } : {}),
      });

      // 2. Persist onto the Firestore appUsers/{uid} record so it survives
      //    across devices and shows up in Admin → Users. Merge so we never
      //    clobber role/status set by provisionUser.
      if (firestore) {
        try {
          await setDoc(
            doc(firestore, 'appUsers', current.uid),
            {
              displayName: nextDisplayName,
              photoURL: nextPhotoURL || null,
              updatedAt: new Date().toISOString(),
            },
            { merge: true },
          );
        } catch (err) {
          console.warn('Failed to persist profile to Firestore', err);
        }
      }

      // 3. Update local session state immediately.
      setState((prev) => {
        if (prev.status !== 'signed-in') return prev;
        return {
          status: 'signed-in',
          user: {
            ...prev.user,
            displayName: nextDisplayName || prev.user.displayName,
            photoURL: photoChanged
              ? nextPhotoURL || undefined
              : prev.user.photoURL,
          },
        };
      });
    },
    [],
  );

  const api = useMemo<AuthApi>(
    () => ({
      state,
      user: state.status === 'signed-in' ? state.user : null,
      hasRole: (role: AuthRole) =>
        state.status === 'signed-in' && roleAtLeast(state.user.role, role),
      signIn,
      signInWithGoogle,
      continueWithoutAuth,
      signOut,
      updateProfile,
    }),
    [state, signIn, signInWithGoogle, continueWithoutAuth, signOut, updateProfile],
  );

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

const ROLE_RANK: Record<AuthRole, number> = {
  admin: 3,
  staff: 2,
  viewer: 1,
};
export function roleAtLeast(actual: AuthRole, required: AuthRole): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}

export const ALL_ROLES: AuthRole[] = ['admin', 'staff', 'viewer'];
